import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";

import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import { ButtonNew as Button } from "components/buttonNew";
import { IRepresentativeForm } from "../../teeOnPaymentsTypes";
import "./representativesStep.scss";
import DataTable from "components/dataTable/DataTable";
import { Badge } from "components/badge/Badge";
import { capitalize } from "helpers/Helpers";
import { t } from "i18next";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import SelectNew from "components/select/SelectNew";
import { ICountry, IProvince } from "redux/reducers/models/customer";
import {
  isValidEmail,
  isValidPhone,
  isValidPostalCode,
  isValidTaxId,
  isValidDateOfBirth,
  getDateOfBirthInvalidReason,
} from "../../teeOnPaymentsValidation";

const defaultRepresentative: IRepresentativeForm = {
  firstName: "",
  lastName: "",
  email: "",
  relationshipType: "representative",
  dateOfBirth: null,
  homeAddress: "",
  city: "",
  country: "",
  province: "",
  county: "",
  postalCode: "",
  phoneNumber: "",
  sin: "",
  ssn: "",
  status: "pending",
};

interface IRepresentativesStepProps {
  representatives: IRepresentativeForm[];
  countries: ICountry[] | undefined;
  onAddRepresentative: (nextRepresentative: IRepresentativeForm) => void;
  onRemoveRepresentative: (index: number) => void;
  onContinue: () => Promise<boolean>;
  onBackToPayoutConfiguration: () => void;
  isSaving: boolean;
}

export default function RepresentativesStep(props: IRepresentativesStepProps) {
  const {
    representatives,
    countries,
    onAddRepresentative,
    onRemoveRepresentative,
    onContinue,
    onBackToPayoutConfiguration,
    isSaving,
  } = props;

  const [formValues, setFormValues] = useState<IRepresentativeForm>(defaultRepresentative);

  const provinces = useMemo(() => {
    if (!countries || !formValues.country) {
      return [];
    }
    const country = countries.find(c => c.id === Number(formValues.country));
    return (country?.provinces ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [countries, formValues.country]);
  const [showAddForm, setShowAddForm] = useState(representatives.length === 0);

  const hasRepresentatives = representatives.length > 0;
  const selectedCountry =
    countries && formValues.country ? countries.find(country => country.id === Number(formValues.country)) : undefined;
  const isUS = selectedCountry?.code === "US";

  const [dirty, setDirty] = useState({
    firstName: false,
    lastName: false,
    email: false,
    relationshipType: false,
    dateOfBirth: false,
    homeAddress: false,
    phoneNumber: false,
    country: false,
    province: false,
    city: false,
    postalCode: false,
    sin: false,
    ssn: false,
  });

  const isNonEmptyString = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;
  const isValidNumericIdString = (value: unknown): value is string =>
    isNonEmptyString(value) && Number.isFinite(Number(value));
  const countryCode = selectedCountry?.code ?? "";

  const isFirstNameValid = isNonEmptyString(formValues.firstName);
  const isLastNameValid = isNonEmptyString(formValues.lastName);
  const isEmailFieldValid = formValues.email.trim().length > 0 ? isValidEmail(formValues.email) : false;
  const isRelationshipTypeValid = isNonEmptyString(formValues.relationshipType);
  const isDateOfBirthFieldValid = isValidDateOfBirth(formValues.dateOfBirth);
  const isHomeAddressValid = isNonEmptyString(formValues.homeAddress);
  const isPhoneNumberFieldValid =
    formValues.phoneNumber.trim().length > 0 ? isValidPhone(formValues.phoneNumber) : false;
  const isCountryValid = isValidNumericIdString(formValues.country);
  const isProvinceValid = isValidNumericIdString(formValues.province);
  const isCityValid = isNonEmptyString(formValues.city);
  const isPostalCodeFieldValid = countryCode
    ? isValidPostalCode(formValues.postalCode, countryCode)
    : isNonEmptyString(formValues.postalCode);
  const isTaxIdValid = isValidTaxId(isUS ? formValues.ssn : formValues.sin);

  const dateOfBirthHelpText =
    dirty.dateOfBirth && !isDateOfBirthFieldValid
      ? (() => {
          const reason = getDateOfBirthInvalidReason(formValues.dateOfBirth);
          switch (reason) {
            case "future":
              return t("Date of birth cannot be in the future.");
            case "too_young":
              return t("Representative must be at least 13 years old.");
            case "too_old":
              return t("Please enter a valid date of birth.");
            default:
              return t("Select a valid date of birth.");
          }
        })()
      : undefined;

  const isAddRepresentativeDisabled =
    !isFirstNameValid ||
    !isLastNameValid ||
    !isEmailFieldValid ||
    !isRelationshipTypeValid ||
    !isDateOfBirthFieldValid ||
    !isHomeAddressValid ||
    !isPhoneNumberFieldValid ||
    !isCountryValid ||
    !isProvinceValid ||
    !isCityValid ||
    !isPostalCodeFieldValid ||
    !isTaxIdValid;

  useEffect(() => {
    if (representatives.length === 0) {
      setShowAddForm(true);
    }
  }, [representatives.length]);

  useEffect(() => {
    if (!countries || formValues.country) {
      return;
    }

    const canada = countries.find(country => country.code === "CA");

    if (canada) {
      setFormValues(prev => ({
        ...prev,
        country: String(canada.id),
      }));
    }
  }, [countries, formValues.country]);

  useEffect(() => {
    if (isUS && formValues.sin) {
      setFormValues(prev => ({ ...prev, sin: "" }));
    }

    if (!isUS && formValues.ssn) {
      setFormValues(prev => ({ ...prev, ssn: "" }));
    }
  }, [isUS]);

  const relationshipTypes = [
    {
      title: t("Director"),
      value: "director",
    },
    {
      title: t("Executive"),
      value: "executive",
    },
    {
      title: t("Owner"),
      value: "owner",
    },
    {
      title: t("Representative"),
      value: "representative",
    },
  ];

  function updateField<K extends keyof IRepresentativeForm>(field: K, value: IRepresentativeForm[K]) {
    setFormValues(prev => ({
      ...prev,
      [field]: value,
    }));
  }

  function addRepresentative() {
    if (isAddRepresentativeDisabled) {
      return;
    }

    onAddRepresentative(formValues);
    setFormValues(defaultRepresentative);
    setShowAddForm(false);
  }

  function handleCancelAdd() {
    if (!hasRepresentatives) {
      onBackToPayoutConfiguration();
      return;
    }

    setShowAddForm(false);
  }

  return (
    <Card title="Add representatives" subtitle="Enter the legal details to add your authorized representatives.">
      {showAddForm ? (
        <>
          <Card.Section noSeparator>
            <div style={{ marginTop: "-24px" }}>
              <hr />
            </div>
            <div className="tee-on-payments-representatives-callout-container">
              <div className="tee-on-payments-representatives-step-info-icon">
                <img src="/public/images/icons/infoCircle.svg" alt="info" width={30} />
              </div>

              <p className="text-sm text-regular text-gray-600">
                {t(
                  "This account should be activated by someone authorized to sign on your organization's behalf. If that's not you, please ask the right person to complete this form.",
                )}
              </p>
            </div>

            <p className="text-md text-semibold text-gray-900 mb-3 mt-4">Identity and role</p>

            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    value={formValues.firstName}
                    label="Legal first name"
                    id="rep_first_name"
                    error={dirty.firstName && !isFirstNameValid}
                    onChange={e => {
                      const next = e.target.value;
                      setDirty(prev => ({ ...prev, firstName: prev.firstName || next.trim().length > 0 }));
                      updateField("firstName", next);
                    }}
                    placeholder="Olivia"
                  />
                  <Input
                    value={formValues.lastName}
                    label="Legal last name"
                    id="rep_last_name"
                    error={dirty.lastName && !isLastNameValid}
                    onChange={e => {
                      const next = e.target.value;
                      setDirty(prev => ({ ...prev, lastName: prev.lastName || next.trim().length > 0 }));
                      updateField("lastName", next);
                    }}
                    placeholder="Rhye"
                  />
                </FormLayout.Group>
              </FormLayout>
            </Form>

            <div className="grid grid-cols-2 gap-[2rem] mb-[24px]">
              <div className="w-full">
                <Input
                  value={formValues.email}
                  label="Email"
                  id="rep_email"
                  error={dirty.email && !isEmailFieldValid}
                  onChange={e => {
                    const next = e.target.value;
                    setDirty(prev => ({ ...prev, email: prev.email || next.trim().length > 0 }));
                    updateField("email", next);
                  }}
                  placeholder="o.rhye@email.com"
                  helpText={dirty.email && !isEmailFieldValid ? t("Enter a valid email address") : undefined}
                />
              </div>

              <div className="w-full">
                <SelectNew
                  id="relationship_type"
                  label="Job title"
                  value={formValues.relationshipType}
                  error={dirty.relationshipType && !isRelationshipTypeValid}
                  onChange={(nextRelationshipType: "director" | "executive" | "owner" | "representative") => {
                    setDirty(prev => ({ ...prev, relationshipType: true }));
                    updateField("relationshipType", nextRelationshipType);
                  }}
                  options={relationshipTypes.map(relationship => ({
                    id: relationship.value,
                    label: relationship.title,
                  }))}
                  placeholder={t("Select job title")}
                />
              </div>
            </div>
            <div>
              <hr />
            </div>

            <p className="text-md text-semibold text-gray-900 mb-3 mt-4">Personal details</p>
            <Form>
              <FormLayout>
                <FormLayout.Group>
                  <DatePickerInput
                    label="Date of birth"
                    error={dirty.dateOfBirth && !isDateOfBirthFieldValid}
                    showBlurTitleOnError
                    helpText={dateOfBirthHelpText}
                    months={1}
                    position="left"
                    startingDate={formValues.dateOfBirth ?? new Date()}
                    setStartingDate={(nextDate: Date) => {
                      setDirty(prev => ({ ...prev, dateOfBirth: true }));
                      updateField("dateOfBirth", nextDate);
                    }}
                  />
                </FormLayout.Group>
              </FormLayout>

              <div className="grid grid-cols-2 gap-[2rem] mb-[16px]">
                <div className="w-full">
                  <Input
                    value={formValues.homeAddress}
                    label="Home address"
                    id="rep_home_address"
                    error={dirty.homeAddress && !isHomeAddressValid}
                    onChange={e => {
                      const next = e.target.value;
                      setDirty(prev => ({ ...prev, homeAddress: prev.homeAddress || next.trim().length > 0 }));
                      updateField("homeAddress", next);
                    }}
                    placeholder="Enter home address"
                  />
                </div>

                <div className="w-full">
                  <Input
                    value={formValues.phoneNumber}
                    label="Phone number"
                    id="rep_phone_number"
                    error={dirty.phoneNumber && !isPhoneNumberFieldValid}
                    onChange={e => {
                      const next = e.target.value;
                      setDirty(prev => ({ ...prev, phoneNumber: prev.phoneNumber || next.trim().length > 0 }));
                      updateField("phoneNumber", next);
                    }}
                    placeholder="+1 (555) 123-4567"
                    helpText={
                      dirty.phoneNumber && !isPhoneNumberFieldValid
                        ? t("Enter a valid phone number with at least 10 digits")
                        : undefined
                    }
                  />
                </div>
              </div>

              <FormLayout>
                <FormLayout.Group>
                  <SelectNew
                    id="rep_country"
                    label="Country"
                    value={formValues.country ? Number(formValues.country) : null}
                    error={dirty.country && !isCountryValid}
                    onChange={(nextCountryId: number) => {
                      setFormValues(prev => ({
                        ...prev,
                        country: String(nextCountryId),
                        province: "",
                      }));
                      setDirty(prev => ({ ...prev, country: true, province: false }));
                    }}
                    options={(countries ?? []).map((country: ICountry) => ({ id: country.id, label: country.name }))}
                    placeholder={t("Select country")}
                  />

                  <SelectNew
                    id="rep_province"
                    label={isUS ? "State" : "Province"}
                    value={formValues.province ? Number(formValues.province) : null}
                    error={dirty.province && !isProvinceValid}
                    onChange={(nextProvinceId: number) => {
                      setDirty(prev => ({ ...prev, province: true }));
                      updateField("province", String(nextProvinceId));
                    }}
                    options={provinces.map((province: IProvince) => ({ id: province.id, label: province.name }))}
                    placeholder={isUS ? t("Select state") : t("Select province")}
                    disabled={!formValues.country}
                  />
                </FormLayout.Group>
              </FormLayout>

              <div className="grid grid-cols-2 gap-[2rem] mb-[16px]">
                <div className="w-full">
                  <Input
                    value={formValues.city}
                    label="City"
                    id="rep_city"
                    error={dirty.city && !isCityValid}
                    onChange={e => {
                      const next = e.target.value;
                      setDirty(prev => ({ ...prev, city: prev.city || next.trim().length > 0 }));
                      updateField("city", next);
                    }}
                    placeholder="Enter city..."
                  />
                </div>

                <div className="w-full">
                  <Input
                    value={formValues.postalCode}
                    label={isUS ? "Zip code" : "Postal code"}
                    id="rep_postal_code"
                    error={dirty.postalCode && !isPostalCodeFieldValid}
                    onChange={e => {
                      const next = e.target.value;
                      setDirty(prev => ({ ...prev, postalCode: prev.postalCode || next.trim().length > 0 }));
                      updateField("postalCode", next);
                    }}
                    placeholder={isUS ? "12345" : "A1A 1A1"}
                    helpText={
                      dirty.postalCode && !isPostalCodeFieldValid
                        ? isUS
                          ? t("Enter a valid US zip code (e.g. 12345)")
                          : t("Enter a valid Canadian postal code (e.g. A1A 1A1)")
                        : undefined
                    }
                  />
                </div>
              </div>
            </Form>

            <p className="text-md text-semibold text-gray-900 mb-3 mt-4">Verification tax ID</p>

            <Form>
              <FormLayout>
                {isUS ? (
                  <FormLayout.Group>
                    <Input
                      value={formValues.ssn}
                      label="Social Security Number (SSN)"
                      id="rep_ssn"
                      error={dirty.ssn && !isTaxIdValid}
                      onChange={e => {
                        const next = e.target.value;
                        setDirty(prev => ({ ...prev, ssn: prev.ssn || next.trim().length > 0 }));
                        updateField("ssn", next);
                      }}
                      placeholder="Enter full 9-digit number here"
                      helpText={dirty.ssn && !isTaxIdValid ? t("SSN must be exactly 9 digits") : undefined}
                    />
                  </FormLayout.Group>
                ) : (
                  <FormLayout.Group>
                    <Input
                      value={formValues.sin}
                      label="Social Insurance Number (SIN)"
                      id="rep_sin"
                      error={dirty.sin && !isTaxIdValid}
                      onChange={e => {
                        const next = e.target.value;
                        setDirty(prev => ({ ...prev, sin: prev.sin || next.trim().length > 0 }));
                        updateField("sin", next);
                      }}
                      placeholder="Enter full 9-digit number here"
                      helpText={dirty.sin && !isTaxIdValid ? t("SIN must be exactly 9 digits") : undefined}
                    />
                  </FormLayout.Group>
                )}
              </FormLayout>
            </Form>
          </Card.Section>

          <Card.Section noSeparator>
            <Button
              type="primary"
              className="w-full justify-center"
              onClick={addRepresentative}
              disabled={isAddRepresentativeDisabled}
            >
              Add representative
            </Button>
            <div className="mt-3">
              <Button type="secondary" className="w-full justify-center" onClick={handleCancelAdd}>
                Cancel
              </Button>
            </div>
          </Card.Section>
        </>
      ) : (
        <>
          <Card.Section noSeparator>
            <div className="tee-on-payments-representatives-table">
              <DataTable
                columns={[
                  { label: "Name", width: "22%" },
                  { label: "Role", width: "20%" },
                  { label: "Email", width: "22%" },
                  { label: "Status", width: "18%" },
                  { label: "", width: "18%" },
                ]}
              >
                {representatives.map((representative, index) => (
                  <tr key={`${representative.email}-${index}`}>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="tee-on-payments-representatives-pill-avatar">
                          <span className="text-sm text-gray-600">
                            {representative.firstName.charAt(0)}
                            {representative.lastName.charAt(0)}
                          </span>
                        </div>

                        <p className="text-sm text-gray-700 text-semibold">
                          {representative.firstName} {representative.lastName}
                        </p>
                      </div>
                    </td>
                    <td className="text-sm text-gray-600 text-regular">
                      {representative.relationshipType || "Representative"}
                    </td>
                    <td className="text-sm text-gray-600 text-regular">{representative.email}</td>
                    <td>
                      <Badge type={representative.status === "success" ? "success" : "warning"} size="small">
                        {capitalize(representative.status)}
                      </Badge>
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="tee-on-payments-representatives-remove"
                        onClick={() => onRemoveRepresentative(index)}
                        aria-label={t("Remove representative")}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </td>
                  </tr>
                ))}
              </DataTable>
            </div>

            <button className="tee-on-payments-add-more mt-4" type="button" onClick={() => setShowAddForm(true)}>
              <FontAwesomeIcon icon={["far", "plus"]} />
              {t("Add more")}
            </button>
          </Card.Section>

          <Card.Section noSeparator>
            <Button
              type="primary"
              className="w-full justify-center"
              disabled={isSaving}
              onClick={() => void onContinue()}
            >
              Save and continue
            </Button>

            <div className="mt-3">
              <Button type="secondary" className="w-full justify-center" onClick={onBackToPayoutConfiguration}>
                Cancel
              </Button>
            </div>
          </Card.Section>
        </>
      )}
    </Card>
  );
}
